import { css } from "@emotion/css";
import { ActionButton, ActionButtonType, Button, formatExternalUrl, resetStyles } from "@octopusdeploy/design-system-components";
import { PlusIcon } from "@octopusdeploy/design-system-icons";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { SpaceResource } from "@octopusdeploy/octopus-server-client";
import { nameForLink, useDispatchLinkClicked } from "@octopusdeploy/portal-routes";
import React from "react";
import { DropdownSwitcherListLayout } from "../../Dropdown/DropdownSwitcherListLayout";
import { NoSearchResults } from "../../Search/NoSearchResults";
import { SpaceList } from "./SpaceList";
export interface SpaceSearchPanelProps {
    spaces: SpaceResource[];
    canCreateNewSpace: boolean;
    isWithinLicenseLimit: boolean;
    onNewSpaceRequested: () => void;
    isLoading: boolean;
    errorAlert?: React.ReactNode;
    searchValue: string;
    onSearchValueChanged: (value: string) => void;
    onSpaceSelected?: () => void;
    licenseSerialNumber?: string;
}
export function SpaceSearchPanel({ spaces, canCreateNewSpace, isWithinLicenseLimit, onNewSpaceRequested, isLoading, errorAlert, searchValue, onSearchValueChanged, onSpaceSelected, licenseSerialNumber }: SpaceSearchPanelProps) {
    const filteredSpaces = React.useMemo(() => filterSpaces(spaces, searchValue), [spaces, searchValue]);
    const noResults = searchValue.length > 0 && filteredSpaces.length === 0;
    const upgradeLinkLabel = "Upgrade";
    const upgradePlanLink = licenseSerialNumber ? formatExternalUrl(`plan-upgrade?serial=${licenseSerialNumber}`) : formatExternalUrl("ControlCenter");
    const dispatchLinkClicked = useDispatchLinkClicked();
    return (<DropdownSwitcherListLayout searchPlaceholder="Quick search all spaces" searchValue={searchValue} onSearchValueChanged={onSearchValueChanged} searchAccessibleName="Search spaces" actionButton={canCreateNewSpace && <ActionButton label="Add space" type={ActionButtonType.Secondary} icon={<PlusIcon size={24}/>} onClick={onNewSpaceRequested}/>} renderList={(listRef) => (<>
                    {noResults ? <NoSearchResults searchInput={searchValue}/> : <SpaceList spaces={filteredSpaces} ref={listRef} onSpaceSelected={onSpaceSelected}/>}
                    {!isWithinLicenseLimit && (<div className={contentStyles}>
                            <p className={upgradePlanCopyStyles}>Upgrade to add more spaces.</p>
                            <div className={upgradePlanStyles}>
                                <Button label={"Upgrade"} importance="loud" href={upgradePlanLink} external={true} onClick={() => dispatchLinkClicked(nameForLink(), {
                    linkLabel: upgradeLinkLabel,
                    documentUrl: upgradePlanLink,
                })}/>
                            </div>
                        </div>)}
                </>)} isLoading={isLoading} errorAlert={errorAlert}/>);
}
function filterSpaces(spaces: SpaceResource[], filter: string) {
    const query = filter.toLowerCase().trim();
    if (!query)
        return spaces;
    return spaces.filter(({ Name, Description }: SpaceResource) => Name.toLowerCase().includes(query) || Description?.toLowerCase().includes(query));
}
const contentStyles = css({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: space[16],
    padding: space[16],
});
const upgradePlanCopyStyles = css({
    ...resetStyles.paragraph,
    font: text.body.regular.large,
    color: themeTokens.color.text.secondary,
    textAlign: "center",
    maxWidth: "24rem",
});
const upgradePlanStyles = css({
    display: "flex",
    gap: space[16],
    alignItems: "center",
});
